import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, TrendingUp, Star, Flame, BookOpen, CheckCircle,
  XCircle, LogOut, Plus, Printer, ChevronDown, ChevronUp,
  AlertTriangle, Trophy, Target
} from "lucide-react";
import { API_BASE_URL } from "../../lib/config";

const API_URL = API_BASE_URL;

// ── Printable Report Component ─────────────────────────────────────────
const PrintableReport = ({ report, onClose }) => {
  const printRef = useRef();

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open("", "_blank");
    win.document.write(`
      <html>
        <head>
          <title>ClearPath Progress Report — ${report.student.name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; color: #1e293b; }
            h1 { font-size: 22px; margin-bottom: 4px; }
            h2 { font-size: 16px; color: #475569; margin-top: 24px; margin-bottom: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; }
            .meta { color: #64748b; font-size: 13px; margin-bottom: 24px; }
            .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
            .summary-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; text-align: center; }
            .summary-card .val { font-size: 24px; font-weight: bold; color: #1e293b; }
            .summary-card .lbl { font-size: 11px; color: #64748b; margin-top: 2px; }
            table { width: 100%; border-collapse: collapse; font-size: 13px; }
            th { background: #f1f5f9; padding: 8px 12px; text-align: left; font-weight: 600; color: #475569; }
            td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; }
            .badge-mastered { background: #dcfce7; color: #166534; padding: 2px 8px; border-radius: 99px; font-size: 11px; font-weight: 600; }
            .badge-learning { background: #fef3c7; color: #92400e; padding: 2px 8px; border-radius: 99px; font-size: 11px; font-weight: 600; }
            .footer { margin-top: 32px; font-size: 11px; color: #94a3b8; text-align: center; }
            @media print { body { margin: 20px; } }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const { student, summary, topics, generated_at } = report;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="font-bold text-xl text-slate-800">Progress Report — {student.name}</h2>
          <div className="flex gap-3">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors"
            >
              <Printer className="w-4 h-4" /> Print / Save PDF
            </button>
            <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors">
              Close
            </button>
          </div>
        </div>

        {/* Printable Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          <div ref={printRef}>
            <h1>ClearPath Testing Solutions — Student Progress Report</h1>
            <div className="meta">
              <strong>{student.name}</strong> &nbsp;|&nbsp; {student.email} &nbsp;|&nbsp; Test: {student.test || "—"}
              &nbsp;|&nbsp; Generated: {new Date(generated_at).toLocaleDateString()}
            </div>

            <h2>Summary</h2>
            <div className="summary-grid">
              <div className="summary-card">
                <div className="val">{summary.overall_accuracy}%</div>
                <div className="lbl">Overall Accuracy</div>
              </div>
              <div className="summary-card">
                <div className="val">{summary.total_questions}</div>
                <div className="lbl">Questions Answered</div>
              </div>
              <div className="summary-card">
                <div className="val">{summary.topics_mastered}/{summary.topics_total}</div>
                <div className="lbl">Topics Mastered</div>
              </div>
              <div className="summary-card">
                <div className="val">Lv. {student.level}</div>
                <div className="lbl">{student.xp} XP — {student.streak}🔥 streak</div>
              </div>
            </div>

            <h2>Topic Breakdown</h2>
            <table>
              <thead>
                <tr>
                  <th>Topic</th>
                  <th>Questions</th>
                  <th>Correct</th>
                  <th>Accuracy</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {topics.map((t) => (
                  <tr key={t.topic}>
                    <td>{t.topic}</td>
                    <td>{t.questions_answered}</td>
                    <td>{t.correct}</td>
                    <td>{t.accuracy}%</td>
                    <td>
                      {t.mastered
                        ? <span className="badge-mastered">✓ Mastered</span>
                        : <span className="badge-learning">In Progress</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="footer">
              ClearPath Testing Solutions &nbsp;·&nbsp; clearpathtest.com &nbsp;·&nbsp; Practice, get help when stuck, and know when you're ready.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Student Card ───────────────────────────────────────────────────────
const StudentCard = ({ student, token, onReportLoad }) => {
  const [expanded, setExpanded] = useState(false);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const daysInactive = student.last_activity
    ? Math.floor((Date.now() - new Date(student.last_activity)) / 86400000)
    : 999;

  const isAtRisk = student.accuracy < 60 || daysInactive >= 5;

  const handleLoadReport = async () => {
    if (report) { onReportLoad(report); return; }
    setLoadingReport(true);
    try {
      const res = await fetch(`${API_URL}/parent/report/${student.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setReport(data);
      onReportLoad(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingReport(false);
    }
  };

  return (
    <div className={`bg-white rounded-2xl border-2 ${isAtRisk ? "border-orange-200" : "border-slate-200"} shadow-sm overflow-hidden`}>
      {/* Card Header */}
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
              {student.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-bold text-slate-800">{student.name}</p>
              <p className="text-slate-400 text-xs">{student.email}</p>
            </div>
          </div>
          {isAtRisk && (
            <div className="flex items-center gap-1 text-orange-500 text-xs font-semibold bg-orange-50 px-3 py-1 rounded-full">
              <AlertTriangle className="w-3 h-3" /> Needs Attention
            </div>
          )}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-3 mt-4">
          {[
            { icon: TrendingUp, label: "Accuracy", value: `${student.accuracy}%`, color: student.accuracy >= 80 ? "text-green-600" : student.accuracy >= 60 ? "text-yellow-600" : "text-red-500" },
            { icon: BookOpen, label: "Questions", value: student.total_questions, color: "text-indigo-600" },
            { icon: Flame, label: "Streak", value: `${student.streak}d`, color: "text-orange-500" },
            { icon: Trophy, label: "Level", value: student.level, color: "text-purple-600" },
          ].map((s) => (
            <div key={s.label} className="bg-slate-50 rounded-xl p-3 text-center">
              <s.icon className={`w-4 h-4 mx-auto mb-1 ${s.color}`} />
              <p className={`font-bold text-sm ${s.color}`}>{s.value}</p>
              <p className="text-slate-400 text-xs">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Mastered Topics */}
        {student.mastered_topics.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {student.mastered_topics.slice(0, 4).map((t) => (
              <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                <CheckCircle className="w-3 h-3" /> {t}
              </span>
            ))}
            {student.mastered_topics.length > 4 && (
              <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs">+{student.mastered_topics.length - 4} more</span>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between bg-slate-50">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-slate-500 hover:text-slate-800 text-sm font-medium transition-colors"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {expanded ? "Hide details" : "View details"}
        </button>
        <button
          onClick={handleLoadReport}
          disabled={loadingReport}
          className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50"
        >
          <Printer className="w-3.5 h-3.5" />
          {loadingReport ? "Loading..." : "Print Report"}
        </button>
      </div>

      {/* Expanded topic list */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-4 mb-3">All Topics</p>
          <div className="space-y-2">
            {student.mastered_topics.length === 0 && (
              <p className="text-slate-400 text-sm">No topics practiced yet.</p>
            )}
            {student.mastered_topics.map((t) => (
              <div key={t} className="flex items-center justify-between text-sm">
                <span className="text-slate-700">{t}</span>
                <span className="flex items-center gap-1 text-green-600 font-medium"><CheckCircle className="w-3.5 h-3.5" /> Mastered</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Parent Dashboard ──────────────────────────────────────────────
const ParentDashboard = () => {
  const [parent, setParent] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addEmail, setAddEmail] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState("");
  const [addSuccess, setAddSuccess] = useState("");
  const [activeReport, setActiveReport] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Auth state
  const [authMode, setAuthMode] = useState("login"); // "login" | "register"
  const [authData, setAuthData] = useState({ name: "", email: "", password: "", student_emails: "" });
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const navigate = useNavigate();

  const getToken = () => localStorage.getItem("parentToken");

  useEffect(() => {
    const token = getToken();
    if (token) {
      setIsLoggedIn(true);
      fetchData(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchData = async (token) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [meRes, studentsRes] = await Promise.all([
        fetch(`${API_URL}/parent/me`, { headers }),
        fetch(`${API_URL}/parent/students`, { headers }),
      ]);
      if (!meRes.ok) { handleLogout(); return; }
      setParent(await meRes.json());
      setStudents(studentsRes.ok ? await studentsRes.json() : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError("");
    try {
      const endpoint = authMode === "login" ? "/parent/login" : "/parent/register";
      const body = authMode === "login"
        ? { email: authData.email, password: authData.password }
        : {
            name: authData.name,
            email: authData.email,
            password: authData.password,
            student_emails: authData.student_emails.split(",").map(e => e.trim()).filter(Boolean),
          };

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Authentication failed");
      localStorage.setItem("parentToken", data.token);
      setIsLoggedIn(true);
      fetchData(data.token);
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("parentToken");
    setIsLoggedIn(false);
    setParent(null);
    setStudents([]);
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    setAddError("");
    setAddSuccess("");
    try {
      const res = await fetch(`${API_URL}/parent/add-student`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ email: addEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to add student");
      setAddSuccess(`${data.student.name} added successfully!`);
      setAddEmail("");
      fetchData(getToken());
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAddLoading(false);
    }
  };

  // ── Auth Screen ──────────────────────────────────────────────────────
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-black text-2xl">C</span>
              </div>
              <span className="font-heading font-black text-2xl text-slate-800">ClearPath</span>
            </Link>
          </div>

          <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="font-heading font-bold text-2xl text-slate-800">Parent Dashboard</h1>
              <p className="text-slate-500 mt-1">Track your student's progress</p>
            </div>

            {/* Tab toggle */}
            <div className="flex rounded-xl bg-slate-100 p-1 mb-6">
              {["login", "register"].map((m) => (
                <button
                  key={m}
                  onClick={() => { setAuthMode(m); setAuthError(""); }}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${authMode === m ? "bg-white shadow text-slate-800" : "text-slate-500"}`}
                >
                  {m === "login" ? "Sign In" : "Create Account"}
                </button>
              ))}
            </div>

            {authError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{authError}</div>
            )}

            <form onSubmit={handleAuth} className="space-y-4">
              {authMode === "register" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Your Name</label>
                  <input
                    type="text"
                    value={authData.name}
                    onChange={(e) => setAuthData({ ...authData, name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-green-500 focus:ring-0"
                    placeholder="Jane Smith"
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  value={authData.email}
                  onChange={(e) => setAuthData({ ...authData, email: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-green-500 focus:ring-0"
                  placeholder="parent@email.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <input
                  type="password"
                  value={authData.password}
                  onChange={(e) => setAuthData({ ...authData, password: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-green-500 focus:ring-0"
                  placeholder="••••••••"
                  required
                />
              </div>
              {authMode === "register" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Student Email(s)</label>
                  <input
                    type="text"
                    value={authData.student_emails}
                    onChange={(e) => setAuthData({ ...authData, student_emails: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-green-500 focus:ring-0"
                    placeholder="student@email.com, another@email.com"
                  />
                  <p className="text-xs text-slate-400 mt-1">Comma-separate multiple students. You can add more later.</p>
                </div>
              )}
              <button
                type="submit"
                disabled={authLoading}
                className="w-full py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {authLoading ? "Loading..." : authMode === "login" ? "Sign In" : "Create Account"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── Loading ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Main Dashboard ───────────────────────────────────────────────────
  const atRiskCount = students.filter(s => s.accuracy < 60 || Math.floor((Date.now() - new Date(s.last_activity)) / 86400000) >= 5).length;

  return (
    <div className="min-h-screen bg-slate-50">
      {activeReport && (
        <PrintableReport report={activeReport} onClose={() => setActiveReport(null)} />
      )}

      {/* Header */}
      <header className="bg-gradient-to-r from-green-600 to-green-700 text-white py-6">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center font-black text-lg">C</Link>
            <div>
              <h1 className="font-heading font-bold text-xl">Parent Dashboard</h1>
              <p className="text-green-100 text-sm">{parent?.name}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-sm font-medium">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-8">

        {/* Summary bar */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "Students Linked", value: students.length, icon: Users, color: "text-indigo-600 bg-indigo-50" },
            { label: "Needs Attention", value: atRiskCount, icon: AlertTriangle, color: atRiskCount > 0 ? "text-orange-500 bg-orange-50" : "text-green-600 bg-green-50" },
            { label: "Avg Accuracy", value: `${students.length ? Math.round(students.reduce((s, x) => s + x.accuracy, 0) / students.length) : 0}%`, icon: Target, color: "text-purple-600 bg-purple-50" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.color}`}>
                <s.icon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{s.value}</p>
                <p className="text-slate-500 text-sm">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Add Student */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5 text-green-600" /> Link a Student
          </h2>
          <form onSubmit={handleAddStudent} className="flex gap-3">
            <input
              type="email"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              placeholder="Enter student's ClearPath email"
              className="flex-1 px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:border-green-500 focus:ring-0 text-sm"
              required
            />
            <button
              type="submit"
              disabled={addLoading}
              className="px-5 py-2.5 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 text-sm"
            >
              {addLoading ? "Adding..." : "Add Student"}
            </button>
          </form>
          {addError && <p className="text-red-500 text-sm mt-2">{addError}</p>}
          {addSuccess && <p className="text-green-600 text-sm mt-2">{addSuccess}</p>}
        </div>

        {/* Student Cards */}
        {students.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No students linked yet.</p>
            <p className="text-sm mt-1">Add a student above using their ClearPath email.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <h2 className="font-bold text-slate-800">Your Students</h2>
            {students.map((s) => (
              <StudentCard
                key={s.id}
                student={s}
                token={getToken()}
                onReportLoad={setActiveReport}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ParentDashboard;
